'use client'

import StatCards from './StatCards'
import { formatFontFamily } from '@/lib/themeHelpers'
import { useScrollAnimation, scrollAnimationClasses } from '@/lib/useScrollAnimation'

interface HeroSectionProps {
  theme: any;
  radius: number;
  fontFamily: string;
  imageUrl: string;
  content: any;
}

export default function HeroSection({
  theme,
  radius,
  fontFamily,
  imageUrl,
  content
}: HeroSectionProps) {
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation();
  const { ref: imageRef, isVisible: imageVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });

  return (
    <section 
      className="relative isolate overflow-hidden min-h-screen" 
      style={{ 
        background: '#FFFFFF',
        fontFamily: formatFontFamily(fontFamily)
      }}
    >
      {/* 2-Column Layout */}
      <div className="mx-auto max-w-6xl px-6 pt-6 pb-24 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
          
          {/* Left Column - Content */}
          <div 
            ref={contentRef as React.RefObject<HTMLDivElement>}
            className={`text-left ${scrollAnimationClasses.transition} ${contentVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
          >
            <div style={{
              display: 'inline-flex',
              background: 'linear-gradient(to right, rgb(237, 34, 36), rgb(243, 91, 34), rgb(249, 150, 33), rgb(245, 193, 30), rgb(241, 235, 27) 27%, rgb(241, 235, 27), rgb(241, 235, 27) 33%, rgb(99, 199, 32), rgb(12, 155, 73), rgb(33, 135, 141), rgb(57, 84, 165), rgb(97, 55, 155), rgb(147, 40, 142))',
              color: '#FFFFFF',
              padding: '8px 16px',
              borderRadius: radius,
              fontSize: '14px',
              fontWeight: '700',
              letterSpacing: '2%',
              marginBottom: '24px',
              fontFamily: 'Arial, Helvetica, sans-serif',
              alignItems: 'center',
              gap: '8px',
              width: 'fit-content',
              border: 'none'
            }}>
              {content.tag}
            </div>
            <h1 className="mt-6 text-4xl font-semibold leading-tight md:text-5xl lg:text-6xl font-primary" style={{ color: '#103065' }}>
              {content.title}
            </h1>
            <p className="mt-6 text-lg leading-relaxed font-primary" style={{ color: theme.mutedText }}>
              {content.description}
            </p>
            <div className="mt-10">
              <StatCards theme={theme} radius={radius} stats={content.stats} />
            </div>
            <div className="mt-8">
              <button 
                className="px-6 py-3 text-base font-semibold shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.02] font-primary" 
                style={{ 
                  background: theme.button, 
                  color: theme.onButton, 
                  borderRadius: radius,
                  border: 'none'
                }}
                onClick={() => (window as any).ChatWidget?.showChat()}
              >
                {content.ctaText}
              </button>
            </div>
          </div>

          {/* Right Column - Image */}
          <div 
            ref={imageRef as React.RefObject<HTMLDivElement>}
            className={`relative ${scrollAnimationClasses.transition} ${imageVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
            style={{ transitionDelay: '200ms' }}
          >
            <div 
              className="w-full h-[500px] lg:h-[600px] rounded-lg shadow-2xl ring-1 ring-black/5"
              style={{
                backgroundImage: `url(${imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                borderRadius: radius
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
